import { ReactNode, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { cn } from '@hqo/react-hooks';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@hqo/ui-atoms';
import { useMobileScreen } from 'app/shared/hooks/use-mobile-screen';

/** Bold the subject + when in the confirm copy so it reads at a glance. */
const renderBold = (chunks: ReactNode): JSX.Element => <strong className="font-semibold">{chunks}</strong>;

interface CancelConfirmModalProps {
  isOpen: boolean;
  /**
   * Catalogue namespace. Reads `activity.<namespace>.` + `title` / `body` / `confirm` / `cancelling` /
   * `keep` / `error` — all six are required, and `check-translations` enforces them across locales.
   */
  namespace: string;
  /** Renders `<testIdPrefix>-modal`, `-confirm`, `-keep`, `-error`. */
  testIdPrefix: string;
  /**
   * Values for the `body` message besides `when` and the bold renderer — normally the one thing being
   * cancelled, under whatever placeholder that namespace's copy uses (`{visitor}`, `{event}`, …).
   */
  subject: Record<string, string>;
  /** Preformatted, e.g. "Tomorrow · 9:00 am". */
  when: string;
  isCancelling: boolean;
  isError: boolean;
  onConfirm: () => void;
  /** Keep / dismiss — overlay click, Esc, or the keep button. */
  onClose: () => void;
}

/**
 * Destructive cancel-confirmation, shared by every cancellable thing in the feed (visits, event
 * bookings, and whatever comes next). Only the copy and the test-id prefix vary, so a new cancellable
 * needs six catalogue keys and nothing here.
 *
 * Three behaviours worth not re-deriving per flow:
 * - initial focus lands on the SAFE action, so a stray Enter on open cannot cancel anything (the
 *   ui-atoms Button doesn't forward refs, hence the query-by-test-id);
 * - a failure renders inline rather than closing, because callers gate on a snapshot the server can
 *   still refuse;
 * - both actions disable while in flight, which is what prevents a double-submit and a mid-request
 *   dismiss — the click never reaches either handler.
 */
export const CancelConfirmModal = ({
  isOpen,
  namespace,
  testIdPrefix,
  subject,
  when,
  isCancelling,
  isError,
  onConfirm,
  onClose,
}: CancelConfirmModalProps): JSX.Element => {
  const intl = useIntl();
  const { isMobileScreen } = useMobileScreen();
  const key = (suffix: string): string => `activity.${namespace}.${suffix}`;

  const focusSafeAction = useCallback(
    (event: Event) => {
      event.preventDefault();
      const root = event.currentTarget as HTMLElement | null;
      root?.querySelector<HTMLButtonElement>(`[data-testid="${testIdPrefix}-keep"]`)?.focus();
    },
    [testIdPrefix],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'border-none p-6',
          isMobileScreen
            ? [
              'bottom-0 left-0 top-auto w-full max-w-full translate-x-0 translate-y-0 rounded-b-none rounded-t-2xl sm:max-w-full',
              'data-[state=open]:fade-in-100 data-[state=closed]:fade-out-100',
              'data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100',
              'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom duration-500',
            ]
            : 'rounded-2xl sm:max-w-[440px]',
        )}
        data-testid={`${testIdPrefix}-modal`}
        onOpenAutoFocus={focusSafeAction}
      >
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl font-semibold text-foreground">
            {intl.formatMessage({ id: key('title') })}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-[15px] leading-6 text-muted-foreground">
          <FormattedMessage id={key('body')} values={{ ...subject, when, b: renderBold }} />
        </DialogDescription>

        {isError && (
          <p className="text-sm text-system-pink" role="alert" data-testid={`${testIdPrefix}-error`}>
            {intl.formatMessage({ id: key('error') })}
          </p>
        )}

        <DialogFooter className="mt-1 flex flex-col gap-2.5 sm:flex-col sm:space-x-0">
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isCancelling}
            className="h-12 w-full text-sm font-semibold"
            data-testid={`${testIdPrefix}-confirm`}
          >
            {intl.formatMessage({ id: key(isCancelling ? 'cancelling' : 'confirm') })}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isCancelling}
            className="h-12 w-full text-sm font-semibold hover:bg-system-grey-light hover:text-foreground"
            data-testid={`${testIdPrefix}-keep`}
          >
            {intl.formatMessage({ id: key('keep') })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
